import { type ReactNode, useState } from 'react';
import { Pencil } from 'lucide-react';
import { useAuthStore } from '../../stores/auth-store';
import { useAdminOverlayStore } from '../../stores/admin-overlay-store';
import { useWebsiteContentOptional } from '../../contexts/WebsiteContentContext';

interface EditableElementProps {
  children: ReactNode;
  section: string;
  field: string;
  type?: 'text' | 'image';
  className?: string;
}

export default function EditableElement({
  children,
  section,
  field,
  type = 'text',
  className = '',
}: EditableElementProps) {
  const { isAdmin } = useAuthStore();
  const { isOverlayEnabled, setEditingElement } = useAdminOverlayStore();
  const content = useWebsiteContentOptional();
  const [isHovered, setIsHovered] = useState(false);

  // If not admin or overlay not enabled, just render children
  if (!isAdmin || !isOverlayEnabled) {
    return <>{children}</>;
  }

  const handleEditClick = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    // Get current value from content context
    let currentValue = '';
    if (content) {
      if (type === 'text') {
        currentValue = content.getContent(section, field, '');
      } else if (type === 'image') {
        const img = content.getImage(`${section}.${field}`);
        currentValue = img?.url ?? '';
      }
    }

    setEditingElement({
      section,
      key: field,
      type,
      currentValue,
    });
  };

  return (
    <div
      className={`relative group ${className}`}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {children}

      {/* Edit button - appears on hover */}
      <button
        onClick={handleEditClick}
        className={`
          absolute top-1 right-1 z-50
          flex items-center gap-1 px-2 py-1 rounded
          bg-gray-900/80 text-white text-xs font-medium
          transition-opacity duration-150
          hover:bg-gray-900
          ${isHovered ? 'opacity-100' : 'opacity-0'}
        `}
        title={`Edit ${section}.${field}`}
      >
        <Pencil className="w-3 h-3" />
        <span>Edit</span>
      </button>
    </div>
  );
}
